import React, { useCallback } from 'react';

import { IconButton } from 'components/common/IconButton';
import { NavButton } from 'components/common/NavButton';
import { BackArrowIcon, CopyIcon } from 'components/common/icons';

interface OwnProps {
  workflowId: string;
  workflowNameDraft: string;
  workflowName: string;
  isDirty: boolean;
  isSaving: boolean;
  isRunnerTabActive: boolean;
  onBackClick: () => void;
  onNameDraftChange: (next: string) => void;
  onNameCommit: (next: string) => void;
  onCopyWorkflowId: () => void;
  onOpenJsonEditor: () => void;
  onToggleRunnerTab: () => void;
  onSaveAsNew: () => void;
}

/**
 * Top app-navbar: brand + workflow name input + workflow id + JSON / Run /
 * Save buttons. Owns the local UX of the name input (blur to commit, Esc
 * to revert, Enter to blur) but delegates persistence to the caller.
 */
const EditorHeader = ({
  workflowId,
  workflowNameDraft,
  workflowName,
  isDirty,
  isSaving,
  isRunnerTabActive,
  onBackClick,
  onNameDraftChange,
  onNameCommit,
  onCopyWorkflowId,
  onOpenJsonEditor,
  onToggleRunnerTab,
  onSaveAsNew,
}: OwnProps) => {
  const handleNameChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => onNameDraftChange(e.target.value),
    [onNameDraftChange],
  );

  const handleNameBlur = useCallback(() => {
    const trimmed = workflowNameDraft.trim();
    if (!trimmed) {
      onNameDraftChange(workflowName);
      return;
    }
    if (trimmed !== workflowName) onNameCommit(trimmed);
  }, [onNameDraftChange, onNameCommit, workflowName, workflowNameDraft]);

  const handleNameKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Enter') e.currentTarget.blur();
      if (e.key === 'Escape') {
        onNameDraftChange(workflowName);
        e.currentTarget.blur();
      }
    },
    [onNameDraftChange, workflowName],
  );

  return (
    <header className='app-navbar'>
      <div className='brand'>
        <img src='/assets/logo-white.svg' alt='Hyarchis' />
        <span className='product'>WORKFLOW BUILDER</span>
      </div>
      <div className='nav-context'>
        <IconButton title='Back to workflow list' onClick={onBackClick}>
          <BackArrowIcon />
        </IconButton>
        <span className='sep' />
        <input
          className='crumb crumb-edit'
          value={workflowNameDraft}
          onChange={handleNameChange}
          onBlur={handleNameBlur}
          onKeyDown={handleNameKeyDown}
          aria-label='Workflow name'
          title='Workflow name — edit and Save as New to create a copy with this name'
        />
        <span className='wf-id' title={workflowId}>
          {workflowId}
        </span>
        <IconButton onClick={onCopyWorkflowId} title='Copy workflow ID'>
          <CopyIcon size={14} />
        </IconButton>
        {isDirty && <span className='unsaved'>unsaved</span>}
      </div>
      <div className='right'>
        <NavButton onClick={onOpenJsonEditor} title='View and edit the raw workflow JSON'>
          JSON
        </NavButton>
        <NavButton
          onClick={onToggleRunnerTab}
          active={isRunnerTabActive}
          title='Run this workflow as a processing job'
        >
          Run
        </NavButton>
        <NavButton
          primary
          onClick={onSaveAsNew}
          disabled={isSaving}
          title='Save as a new workflow (POST /v1/workflows)'
        >
          {isSaving ? 'Saving…' : 'Save as New'}
        </NavButton>
      </div>
    </header>
  );
};

export default React.memo(EditorHeader);
